/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import CryptoJS from "crypto-js";

export const TOAST_REQUEST_ENCRYPTION_PREFIX = "ENCv1";
const TOAST_MASK = "********";
// Trailing group is bounded by length, not by "not an asterisk": the mask copies the
// last four characters verbatim, so those may themselves contain an asterisk.
// Ruby counterpart: ToastRequestCredentialCipher::MASKED_CREDENTIAL_PATTERN.
export const MASKED_SENTINEL_PATTERN = /^\*+[\s\S]{0,4}$/;

export const TOAST_CREDENTIAL_REQUEST_FIELDS = [
  "toast_email",
  "toast_password",
  "toast_authenticator_key",
  "toast_client_id",
  "toast_client_secret_key",
  "seven_shifts_access_token",
];

const getToastRequestEncryptionKey = () => {
  const key =
    process.env.REACT_APP_TOAST_REQUEST_CREDENTIALS_KEY ||
    process.env.REACT_APP_CONFIGURATION_SECRET_KEY;

  if (!key && process.env.NODE_ENV === "production") {
    throw new Error("Missing configuration for REACT_APP_TOAST_REQUEST_CREDENTIALS_KEY");
  }

  const ikm = key || "development_fallback_key_32_bytes_long!";
  
  // Implement HKDF-Expand with SHA256 manually since crypto-js lacks native HKDF
  const salt = CryptoJS.enc.Hex.parse("");
  const prk = CryptoJS.HmacSHA256(ikm, salt);
  const infoParam = CryptoJS.enc.Utf8.parse("toast-request-cipher-v1").clone().concat(CryptoJS.enc.Hex.parse("01"));
  
  return CryptoJS.HmacSHA256(infoParam, prk);
};

export const isEncryptedToastCredentialValue = (value) => {
  return (
    typeof value === "string" &&
    value.startsWith(`${TOAST_REQUEST_ENCRYPTION_PREFIX}:`)
  );
};

export const isMaskedToastCredentialSentinel = (value) => {
  return typeof value === "string" && MASKED_SENTINEL_PATTERN.test(value);
};

export const encryptToastCredentialValue = (value) => {
  if (typeof value !== "string") return value;
  if (value === "" || value === TOAST_MASK || isMaskedToastCredentialSentinel(value) || isEncryptedToastCredentialValue(value)) {
    return value;
  }

  const key = getToastRequestEncryptionKey();
  const iv = CryptoJS.lib.WordArray.random(16);
  
  // CBC was chosen over AES-256-GCM as this is purely for payload obfuscation (shoulder-surfing prevention) 
  // and CryptoJS does not natively support GCM. Implementing Web Crypto API across the frontend exceeded the 
  // constraints and scope for a cosmetic threat model.
  const encrypted = CryptoJS.AES.encrypt(value, key, {
    iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });

  const ivBase64 = CryptoJS.enc.Base64.stringify(iv);
  const cipherTextBase64 = CryptoJS.enc.Base64.stringify(encrypted.ciphertext);

  return `${TOAST_REQUEST_ENCRYPTION_PREFIX}:${ivBase64}:${cipherTextBase64}`;
};

const encryptFieldsInContainer = (container) => {
  if (!container || typeof container !== "object") return container;

  TOAST_CREDENTIAL_REQUEST_FIELDS.forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(container, field)) {
      container[field] = encryptToastCredentialValue(container[field]);
    }
  });

  return container;
};

export const encryptToastCredentialFieldsInObject = (payload) => {
  if (!payload || typeof payload !== "object") return payload;

  const nextPayload = { ...payload };
  encryptFieldsInContainer(nextPayload);

  if (nextPayload.restaurant && typeof nextPayload.restaurant === "object") {
    nextPayload.restaurant = { ...nextPayload.restaurant };
    encryptFieldsInContainer(nextPayload.restaurant);
  }

  return nextPayload;
};

export const encryptToastCredentialFieldsInFormData = (formData) => {
  if (!(formData instanceof FormData)) return formData;

  TOAST_CREDENTIAL_REQUEST_FIELDS.forEach((field) => {
    const fieldKeys = [field, `restaurant[${field}]`];

    fieldKeys.forEach((fieldKey) => {
      if (!formData.has(fieldKey)) return;

      const nextValues = formData
        .getAll(fieldKey)
        .map((value) => (typeof value === "string" ? encryptToastCredentialValue(value) : value));

      formData.delete(fieldKey);
      nextValues.forEach((value) => formData.append(fieldKey, value));
    });
  });

  return formData;
};
